// ============================================================
// API SERVICE — society_hub backend integration
// Base URL is read from REACT_APP_API_URL env var or defaults
// to localhost:5000. Set it in a .env file:
//   REACT_APP_API_URL=http://your-server:5000
// ============================================================

const BASE_URL = process.env.REACT_APP_API_URL || 'https://ghostwhite-gorilla-727185.hostingersite.com';

// ── Token helpers ────────────────────────────────────────────
export const getToken = () => localStorage.getItem('auth_token');
export const setToken = (t) => localStorage.setItem('auth_token', t);
export const clearToken = () => localStorage.removeItem('auth_token');

// ── Core fetch wrapper ───────────────────────────────────────
async function request(method, path, body = null, useAuth = true) {
  const headers = { 'Content-Type': 'application/json' };
  if (useAuth) {
    const token = getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }

  const options = { method, headers };
  if (body) options.body = JSON.stringify(body);

  const res = await fetch(`${BASE_URL}${path}`, options);

  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try { const e = await res.json(); msg = e.message || e.error || msg; } catch (_) {}
    throw new Error(msg);
  }

  // 204 No Content
  if (res.status === 204) return null;
  return res.json();
}

const get    = (path)         => request('GET',    path);
const post   = (path, body)   => request('POST',   path, body);
const put    = (path, body)   => request('PUT',    path, body);
const del    = (path)         => request('DELETE', path);

// ─────────────────────────────────────────────────────────────
// AUTH
// ─────────────────────────────────────────────────────────────
export const AuthAPI = {
  // POST /api/super-admin/login
  superAdminLogin: (email, password) =>
    request('POST', '/api/super-admin/login', { email, password }, false),

  // POST /api/admins/login
  adminLogin: (email, password) =>
    request('POST', '/api/admins/login', { email, password }, false),
};

// ─────────────────────────────────────────────────────────────
// SUPER-ADMIN
// ─────────────────────────────────────────────────────────────
export const SuperAdminAPI = {
  // POST /api/super-admin/create
  create: (data)              => post('/api/super-admin/create', data),
  // GET  /api/super-admin/profile
  getProfile: ()              => get('/api/super-admin/profile'),
  // PUT  /api/super-admin/profile
  updateProfile: (data)       => put('/api/super-admin/profile', data),

  // ── Societies (super-admin view) ──────────────────────────
  // GET  /api/super-admin/societies
  getAllSocieties: ()          => get('/api/super-admin/societies'),
  // GET  /api/super-admin/societies/:id
  getSocietyById: (id)        => get(`/api/super-admin/societies/${id}`),

  // ── Admins (super-admin view) ─────────────────────────────
  // GET  /api/super-admin/admins
  getAllAdmins: ()             => get('/api/super-admin/admins'),
  // GET  /api/super-admin/admins/:id
  getAdminById: (id)          => get(`/api/super-admin/admins/${id}`),
};

// ─────────────────────────────────────────────────────────────
// ADMIN MANAGEMENT  (super-admin manages admins)
// ─────────────────────────────────────────────────────────────
export const AdminMgmtAPI = {
  // POST   /api/admins
  create: (data)              => post('/api/admins', data),
  // GET    /api/admins
  getAll: ()                  => get('/api/admins'),
  // GET    /api/admins/:id/profile
  getProfile: (id)            => get(`/api/admins/${id}/profile`),
  // PUT    /api/admins/:id/status
  updateStatus: (id, data)    => put(`/api/admins/${id}/status`, data),
  // DELETE /api/admins/:id
  delete: (id)                => del(`/api/admins/${id}`),
  // GET    /api/admins/profile  (self)
  getSelfProfile: ()          => get('/api/admins/profile'),
  // PUT    /api/admins/profile  (self)
  updateSelfProfile: (data)   => put('/api/admins/profile', data),
  // PUT    /api/admins/change-password
  changePassword: (data)      => put('/api/admins/change-password', data),
};

// ─────────────────────────────────────────────────────────────
// SOCIETY
// ─────────────────────────────────────────────────────────────
export const SocietyAPI = {
  // POST /api/society/register
  register: (data)            => post('/api/society/register', data),
  // GET  /api/society/my
  getMy: ()                   => get('/api/society/my'),
  // GET  /api/society/:id
  getById: (id)               => get(`/api/society/${id}`),
  // PUT  /api/society/:id
  update: (id, data)          => put(`/api/society/${id}`, data),
};

// ─────────────────────────────────────────────────────────────
// FINANCIAL YEAR
// ─────────────────────────────────────────────────────────────
export const FinancialYearAPI = {
  // POST /api/financial-year
  create: (data)              => post('/api/financial-year', data),
  // GET  /api/financial-year
  getAll: ()                  => get('/api/financial-year'),
  // PUT  /api/financial-year/set-active
  setActive: (financialYearId) => put('/api/financial-year/set-active', { financialYearId }),
  // GET  /api/financial-year/active
  getActive: ()               => get('/api/financial-year/active'),
};

// ─────────────────────────────────────────────────────────────
// ACCOUNT GROUPS
// ─────────────────────────────────────────────────────────────
export const AccountGroupAPI = {
  // POST   /api/accountsgroup
  create: (data)              => post('/api/accountsgroup', data),
  // GET    /api/accountsgroup
  getAll: ()                  => get('/api/accountsgroup'),
  // PUT    /api/accountsgroup/:id
  update: (id, data)          => put(`/api/accountsgroup/${id}`, data),
  // DELETE /api/accountsgroup/:id
  delete: (id)                => del(`/api/accountsgroup/${id}`),
};

// ─────────────────────────────────────────────────────────────
// ACCOUNT SUB-GROUPS
// ─────────────────────────────────────────────────────────────
export const AccountSubGroupAPI = {
  // POST   /api/accountsubgroup
  create: (data)              => post('/api/accountsubgroup', data),
  // GET    /api/accountsubgroup
  getAll: ()                  => get('/api/accountsubgroup'),
  // PUT    /api/accountsubgroup/:id
  update: (id, data)          => put(`/api/accountsubgroup/${id}`, data),
  // POST   /api/accountsubgroup/use-default-sub-groups
  applyDefaultSubGroups: (data)         => post('/api/accountsubgroup/use-default-sub-groups', data),
};

// ─────────────────────────────────────────────────────────────
// BANK / CASH MASTER
// ─────────────────────────────────────────────────────────────
export const BankCashAPI = {
  // POST   /api/bankcashmaster
  create: (data)              => post('/api/bankcashmaster', data),
  // GET    /api/bankcashmaster
  getAll: ()                  => get('/api/bankcashmaster'),
  // GET    /api/bankcashmaster?accountType=BANK|CASH
  getByType: (accountType)    => get(`/api/bankcashmaster?accountType=${accountType}`),
  // PUT    /api/bankcashmaster/:id
  update: (id, data)          => put(`/api/bankcashmaster/${id}`, data),
  // DELETE /api/bankcashmaster/:id
  delete: (id)                => del(`/api/bankcashmaster/${id}`),
};

// ─────────────────────────────────────────────────────────────
// LEDGER / ACCOUNT MASTER
// ─────────────────────────────────────────────────────────────
export const LedgerAPI = {
  // POST   /api/ledger/payment
  createPayment: (data)       => post('/api/ledger/payment', data),
  // GET    /api/ledger/payment
  getPayment: ()              => get('/api/ledger/payment'),
  // PUT    /api/ledger/payment/:id
  updatePayment: (id, data)   => put(`/api/ledger/payment/${id}`, data),
  // DELETE /api/ledger/payment/:id
  deletePayment: (id)         => del(`/api/ledger/payment/${id}`),

  // GET    /api/ledger/regular?societyId=
  getRegular: (societyId)     => get(`/api/ledger/regular?societyId=${societyId}`),
  // GET    /api/ledger/regular/defaults
  getRegularDefaults: ()      => get('/api/ledger/regular/defaults'),

  // POST   /api/ledger/supplementary
  createSupplementary: (data) => post('/api/ledger/supplementary', data),
  // GET    /api/ledger/supplementary?societyId=
  getSupplementary: (societyId) => get(`/api/ledger/supplementary?societyId=${societyId}`),
  // PUT    /api/ledger/supplementary/:id
  updateSupplementary: (id, data) => put(`/api/ledger/supplementary/${id}`, data),
  // DELETE /api/ledger/supplementary/:id
  deleteSupplementary: (id)   => del(`/api/ledger/supplementary/${id}`),
};

// ─────────────────────────────────────────────────────────────
// BUDGET
// ─────────────────────────────────────────────────────────────
export const BudgetAPI = {
  // POST   /api/budget
  create: (data)              => post('/api/budget', data),
  // GET    /api/budget?societyId=
  getAll: (societyId)         => get(`/api/budget?societyId=${societyId}`),
  // PUT    /api/budget/:id
  update: (id, data)          => put(`/api/budget/${id}`, data),
  // DELETE /api/budget/:id
  delete: (id)                => del(`/api/budget/${id}`),
};

// ─────────────────────────────────────────────────────────────
// BILL PARAMETERS
// ─────────────────────────────────────────────────────────────
export const BillParametersAPI = {
  // POST /api/billparameter  (billType: OPENING_REGULAR | OPENING_SUPPLEMENTARY | REGULAR | SUPPLEMENTARY)
  create: (data)              => post('/api/billparameter', data),
};

// ─────────────────────────────────────────────────────────────
// SET CHARGES
// ─────────────────────────────────────────────────────────────
export const SetChargesAPI = {
  // POST /api/setcharges
  create: (data)              => post('/api/setcharges', data),
};

// ─────────────────────────────────────────────────────────────
// BILL DESIGN
// ─────────────────────────────────────────────────────────────
export const BillDesignAPI = {
  // POST /api/billdesign
  create: (data)              => post('/api/billdesign', data),
  // GET  /api/billdesign?billType=SUPPLEMENTARY|REGULAR
  getByType: (billType)       => get(`/api/billdesign?billType=${billType}`),
};

// ─────────────────────────────────────────────────────────────
// UTILITY: extract society ID from stored profile
// ─────────────────────────────────────────────────────────────
export const getSocietyId = () => {
  try {
    const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
    return profile.societyId || profile.society?.id || 1;
  } catch (_) {
    return 1;
  }
};
